<template>
  <modal-wrapper
    ref="modalRef"
    :busy="okButtonBusy"
    :close-on-ok-click="false"
    :content-overflow-y="'visible'"
    :header="title"
    :ok-button-label="t('generic.add')"
    @hide="cleanModal"
    @ok-button-click="handleModalOk"
    @show="handleShow"
  >
    <styled-multiselect
      v-model="selectedUser"
      :options="users"
      :label="formatUserLabel"
      :placeholder="placeholder"
      track-by="oid"
      :loading="isLoadingUsers"
      :is-error="isNoSelectedUserError"
      @search-change="handleSearchChange"
    />

    <message-wrapper v-if="isNoSelectedUserError" simple-error>
      {{ selectionRequiredMessage }}
    </message-wrapper>
  </modal-wrapper>
</template>

<script lang="ts" setup>
  import MessageWrapper from '@/components/MessageWrapper/MessageWrapper.vue';
  import StyledMultiselect from '@/components/StyledMultiselect/StyledMultiselect.vue';
  import { debounce } from 'lodash';
  import { IAdUserInternalFormat } from 'types';
  import { ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useModalWrapper } from '../ModalWrapper';

  const emit = defineEmits<{
    (e: 'selected-user', payload: IAdUserInternalFormat): void;
  }>();

  const props = defineProps<{
    modalId?: string;
    okButtonBusy?: boolean;
    placeholder: string;
    searchUsers: (query: string) => Promise<IAdUserInternalFormat[]>;
    selectionRequiredMessage: string;
    title: string;
  }>();
  const { t } = useI18n();
  const { modalRef, show, hide } = useModalWrapper();

  defineExpose({ show, hide });

  const selectedUser = ref<IAdUserInternalFormat | null>(null);
  const users = ref<IAdUserInternalFormat[]>([]);
  const isLoadingUsers = ref(false);
  const isNoSelectedUserError = ref(false);

  async function loadUsers(term: string): Promise<void> {
    isLoadingUsers.value = true;

    try {
      users.value = await props.searchUsers(term);
    } finally {
      isLoadingUsers.value = false;
    }
  }

  const debouncedLoadUsers = debounce(async (term: string) => {
    await loadUsers(term);
  }, 300);

  function handleModalOk(): void {
    if (!selectedUser.value) {
      isNoSelectedUserError.value = true;
      return;
    }

    isNoSelectedUserError.value = false;
    emit('selected-user', selectedUser.value);
    hide();
    cleanModal();
  }

  function cleanModal(): void {
    debouncedLoadUsers.cancel();
    selectedUser.value = null;
    users.value = [];
    isNoSelectedUserError.value = false;
    isLoadingUsers.value = false;
  }

  function handleShow(): void {
    cleanModal();
    void loadUsers('');
  }

  function handleSearchChange(term: string): void {
    const trimmedTerm = term.trim();

    if (!trimmedTerm) {
      debouncedLoadUsers.cancel();
      if (!selectedUser.value) {
        void loadUsers('');
      }
      return;
    }

    selectedUser.value = null;
    debouncedLoadUsers(trimmedTerm);
  }

  function formatUserLabel(user: IAdUserInternalFormat): string {
    const fullName =
      [user.firstName, user.lastName].filter(Boolean).join(' ').trim() ||
      user.displayName ||
      t('spGroupModal.noData');
    const email = user.email || user.accountEmail;

    return email ? `${fullName} - [${email}]` : fullName;
  }

  watch(selectedUser, (user) => {
    if (user) {
      isNoSelectedUserError.value = false;
    }
  });
</script>
